import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { mergeStyling } from '../../common-functions';
import { InputGroup, InputGroupAddon, InputGroupInput } from '../input-group/input-group';

const Combobox = <Value, Multiple extends boolean | undefined = false>({
  autoHighlight = true,
  onOpenChange,
  ...props
}: ComboboxPrimitive.Root.Props<Value, Multiple>) => {
  const handleOpenChange: NonNullable<typeof onOpenChange> = (open, details) => {
    // base-ui only stopPropagation's the Escape keydown, so a native <dialog> ancestor still
    // closes via the keydown's default action. preventDefault here keeps the dialog open and
    // makes Esc dismiss only the popup.
    if (!open && details.reason === 'escape-key' && details.event instanceof KeyboardEvent) {
      details.event.preventDefault();
    }
    onOpenChange?.(open, details);
  };
  return <ComboboxPrimitive.Root autoHighlight={autoHighlight} onOpenChange={handleOpenChange} {...props} />;
};

const ComboboxValue = ({ ...props }: ComboboxPrimitive.Value.Props) => {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />;
};

const ComboboxTrigger = ({ className, children, ...props }: ComboboxPrimitive.Trigger.Props) => {
  const { t } = useTranslation();
  return (
    <ComboboxPrimitive.Trigger
      aria-label={t('combobox-open')}
      data-slot="combobox-trigger"
      tabIndex={0}
      className={mergeStyling(
        'ml-1 shrink rounded-sm p-1',
        'text-gray-2 transition-colors focus:outline-hidden',
        'focus-visible:ring-2 focus-visible:ring-primary-2',
        'not-data-disabled:hover:bg-gray-5 not-data-disabled:hover:text-gray-1',
        '[&_svg]:size-4',
        'group-has-data-[slot=combobox-clear]/input-group:hidden',
        'in-data-[size=sm]:p-0.5',
        className
      )}
      {...props}
    >
      {children}
      <ChevronDownIcon aria-hidden="true" />
    </ComboboxPrimitive.Trigger>
  );
};

const ComboboxClear = ({ className, ...props }: ComboboxPrimitive.Clear.Props) => {
  const { t } = useTranslation();
  return (
    <ComboboxPrimitive.Clear
      aria-label={t('combobox-clear')}
      data-slot="combobox-clear"
      tabIndex={0}
      className={mergeStyling(
        'ml-1 shrink cursor-pointer rounded-sm p-1',
        'text-gray-2 transition-colors focus:outline-hidden',
        'focus-visible:ring-2 focus-visible:ring-primary-2',
        'not-data-disabled:hover:bg-gray-5 not-data-disabled:hover:text-gray-1',
        '[&_svg]:size-4',
        'in-data-[size=sm]:p-0.5',
        className
      )}
      {...props}
    >
      <XIcon aria-hidden="true" />
    </ComboboxPrimitive.Clear>
  );
};

const ComboboxInput = ({
  className,
  children,
  disabled = false,
  showTrigger = true,
  showClear = false,
  size = 'default',
  ...props
}: Omit<ComboboxPrimitive.Input.Props, 'size'> & {
  showTrigger?: boolean;
  showClear?: boolean;
  size?: 'sm' | 'default';
}) => {
  return (
    <ComboboxPrimitive.InputGroup
      render={
        <InputGroup
          data-size={size}
          className={mergeStyling(
            'h-8 w-full items-center justify-center rounded-sm bg-mono-1 py-1 md:h-8',
            'outline-hidden focus:border-primary-2',
            'has-aria-invalid:border-error',
            'has-disabled:select-none has-disabled:bg-transparent has-disabled:opacity-50',
            'data-[size=sm]:h-7 data-[size=sm]:py-0.5 md:data-[size=sm]:h-7',
            className
          )}
        />
      }
    >
      <ComboboxPrimitive.Input
        render={
          <InputGroupInput
            data-size={size}
            disabled={disabled}
            className="h-8 truncate disabled:bg-transparent data-[size=sm]:h-7 md:h-8 md:data-[size=sm]:h-7"
          />
        }
        {...props}
      />
      <InputGroupAddon align="inline-end">
        {showClear && <ComboboxClear />}
        {showTrigger && <ComboboxTrigger />}
      </InputGroupAddon>
      {children}
    </ComboboxPrimitive.InputGroup>
  );
};

const ComboboxContent = ({
  className,
  side = 'bottom',
  sideOffset = 3,
  align = 'start',
  alignOffset = 0,
  anchor,
  container,
  ...props
}: ComboboxPrimitive.Popup.Props &
  Pick<ComboboxPrimitive.Positioner.Props, 'side' | 'align' | 'sideOffset' | 'alignOffset' | 'anchor'> &
  Pick<ComboboxPrimitive.Portal.Props, 'container'>) => {
  const inDialog =
    typeof HTMLElement !== 'undefined' &&
    container instanceof HTMLElement &&
    container.matches('dialog, [role="dialog"]:not([data-slot="popover-content"])');
  return (
    <ComboboxPrimitive.Portal container={container}>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        // In a dialog, flip runs before size, so a tall list (natural height > room below) flips
        // the popup to the opposite side before size constrains --available-height. Pin it below
        // and let size + the list's scroll handle overflow. Outside dialogs, keep default flip.
        // align-flip and shift still run either way.
        collisionAvoidance={inDialog ? { side: 'none' } : undefined}
        className="isolate z-50"
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          data-chips={!!anchor}
          className={mergeStyling(
            'group/combobox-content relative overflow-hidden',
            'rounded-sm bg-mono-1 text-gray-1 shadow-md ring-1 ring-gray-4',
            'max-h-(--available-height) w-(--anchor-width) max-w-(--available-width) origin-(--transform-origin)',
            'data-[chips=true]:min-w-(--anchor-width)',
            '*:data-[slot=input-group]:m-1 *:data-[slot=input-group]:mb-0',
            '*:data-[slot=input-group]:h-10 md:*:data-[slot=input-group]:h-8',
            '*:data-[slot=input-group]:shadow-none',
            'duration-100',
            'data-open:fade-in-0 data-open:zoom-in-95 data-open:animate-in',
            'data-closed:fade-out-0 data-closed:zoom-out-95 data-closed:animate-out',
            'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
            'data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2',
            'data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2',
            className
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
};

const ComboboxList = ({ ref, className, ...props }: ComboboxPrimitive.List.Props) => {
  return (
    <ComboboxPrimitive.List
      ref={ref}
      data-slot="combobox-list"
      className={mergeStyling(
        'max-h-[min(18rem,calc(var(--available-height)-2.25rem))]',
        'scroll-py-1 overflow-y-auto overscroll-contain p-1 data-empty:p-0',
        className
      )}
      {...props}
    />
  );
};

const ComboboxItem = ({ ref, className, children, ...props }: ComboboxPrimitive.Item.Props) => {
  return (
    <ComboboxPrimitive.Item
      ref={ref}
      data-slot="combobox-item"
      className={mergeStyling(
        'relative flex w-full cursor-default select-none items-center gap-2 rounded-sm',
        'py-1 pr-8 pl-1.5 text-sm outline-hidden',
        'data-highlighted:bg-primary-5 data-highlighted:text-primary-2',
        'not-data-[variant=destructive]:data-highlighted:**:text-primary-2',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        "[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className
      )}
      {...props}
    >
      {children}
      <ComboboxPrimitive.ItemIndicator
        render={
          <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center">
            <CheckIcon className="pointer-events-none" />
          </span>
        }
      />
    </ComboboxPrimitive.Item>
  );
};

const ComboboxGroup = ({ className, ...props }: ComboboxPrimitive.Group.Props) => {
  return <ComboboxPrimitive.Group data-slot="combobox-group" className={mergeStyling(className)} {...props} />;
};

const ComboboxLabel = ({ className, ...props }: ComboboxPrimitive.GroupLabel.Props) => {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-label"
      className={mergeStyling('px-2 py-1.5 text-gray-2 text-xs', className)}
      {...props}
    />
  );
};

const ComboboxCollection = ({ ...props }: ComboboxPrimitive.Collection.Props) => {
  return <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />;
};

const ComboboxEmpty = ({ className, ...props }: ComboboxPrimitive.Empty.Props) => {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={mergeStyling(
        'hidden w-full justify-center py-2 text-center text-gray-2 text-sm group-data-empty/combobox-content:flex',
        className
      )}
      {...props}
    />
  );
};

const ComboboxSeparator = ({ className, ...props }: ComboboxPrimitive.Separator.Props) => {
  return (
    <ComboboxPrimitive.Separator
      data-slot="combobox-separator"
      className={mergeStyling('-mx-1 my-1 h-px bg-gray-4', className)}
      {...props}
    />
  );
};

const ComboboxChips = ({
  className,
  size = 'default',
  ...props
}: React.ComponentPropsWithRef<typeof ComboboxPrimitive.Chips> &
  ComboboxPrimitive.Chips.Props & {
    size?: 'sm' | 'default';
  }) => {
  return (
    <ComboboxPrimitive.Chips
      data-slot="combobox-chips"
      data-size={size}
      className={mergeStyling(
        'group/combobox-chips flex flex-wrap items-center gap-1',
        'min-h-10 px-2.5 py-1.5 text-base md:min-h-8 md:py-1 md:text-sm',
        'rounded-sm border border-gray-4 bg-mono-1 bg-clip-padding transition-colors',
        'focus-within:border-primary-2',
        'has-aria-invalid:border-error',
        'has-data-[slot=combobox-chip]:px-1',
        'data-[size=sm]:min-h-7 data-[size=sm]:py-0.5 data-[size=sm]:text-sm',
        'md:data-[size=sm]:min-h-7 md:data-[size=sm]:py-0.5 md:data-[size=sm]:text-sm',
        className
      )}
      {...props}
    />
  );
};

const ComboboxChip = ({
  className,
  children,
  showRemove = true,
  ...props
}: ComboboxPrimitive.Chip.Props & {
  showRemove?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <ComboboxPrimitive.Chip
      data-slot="combobox-chip"
      className={mergeStyling(
        'flex h-6 w-fit items-center justify-center gap-1 whitespace-nowrap rounded-sm',
        'bg-gray-5 px-1.5 font-medium text-gray-1 text-sm',
        'md:h-[1.3125rem] md:text-xs',
        'has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-disabled:opacity-50',
        className
      )}
      {...props}
    >
      {children}
      {showRemove && (
        <ComboboxPrimitive.ChipRemove
          aria-label={t('combobox-chip-remove')}
          className="ml-0.5 cursor-pointer text-gray-2 opacity-50 hover:opacity-100"
          data-slot="combobox-chip-remove"
        >
          <XIcon className="size-3" />
        </ComboboxPrimitive.ChipRemove>
      )}
    </ComboboxPrimitive.Chip>
  );
};

const ComboboxChipsInput = ({ className, ...props }: ComboboxPrimitive.Input.Props) => {
  return (
    <ComboboxPrimitive.Input
      data-slot="combobox-chip-input"
      className={mergeStyling(
        'min-w-16 flex-1 outline-hidden',
        'group-has-data-[slot=combobox-chip]/combobox-chips:placeholder:text-transparent',
        className
      )}
      {...props}
    />
  );
};

const useComboboxAnchor = () => {
  return React.useRef<HTMLDivElement | null>(null);
};

const useComboboxFilter = ComboboxPrimitive.useFilter;
const useComboboxFilteredItems = ComboboxPrimitive.useFilteredItems;

type ComboboxRootProps<Value, Multiple extends boolean | undefined = false> = ComboboxPrimitive.Root.Props<
  Value,
  Multiple
>;

interface ComboboxPresetBaseProps<T> {
  /** The items to render. */
  items: readonly T[];
  /** Returns a stable React key for each item. */
  getKey: (item: T) => string | number;
  /** Returns the user-visible label for each item. Also passed to base-ui as `itemToStringLabel`. */
  getLabel: (item: T) => string;
  /** Rendered inside the popup when the filter yields no results. Defaults to `t('combobox-empty')`. */
  emptyMessage?: React.ReactNode;
  /** Override the auto-resolved dialog container. */
  container?: HTMLElement;
}

export {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxClear,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  type ComboboxPresetBaseProps,
  type ComboboxRootProps,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxAnchor,
  useComboboxFilter,
  useComboboxFilteredItems,
};
